import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Turtle, Users, Zap, BookOpen, Play, Book, LayoutDashboard, LogIn, LogOut, History, UserCircle, Trophy, Sun, Moon, CalendarDays, Bookmark, ShieldCheck, Award } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import realCasesService from '../services/realCasesService';
import { authService } from '../services/auth';
import { gameAPI } from '../services/api';
import LoginModal from '../components/LoginModal';
import { useTheme } from '../contexts/ThemeContext';

const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const { toggleTheme, isDark } = useTheme();
  const [unlockedCount, setUnlockedCount] = useState(0);
  const [completedCount, setCompletedCount] = useState(0);
  const [totalCount, setTotalCount] = useState(13);
  const [progressPercentage, setProgressPercentage] = useState(0);
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [dailyChallenge, setDailyChallenge] = useState<any>(null);
  const [dailyDone, setDailyDone] = useState(false);
  const [badgeCount, setBadgeCount] = useState(0);

  useEffect(() => {
    const user = authService.getCurrentUser();
    if (user) setCurrentUser(user);
  }, []);

  useEffect(() => {
    // 每日挑战 + 反诈徽章
    gameAPI.getDaily()
      .then(res => {
        if (res.success) {
          setDailyChallenge(res.data);
          const badges = JSON.parse(localStorage.getItem('lts_daily_badges') || '[]');
          setBadgeCount(badges.length);
          setDailyDone(badges.some((b: any) => b.date === res.data.date));
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    const loadProgress = async () => {
      const savedProgress = JSON.parse(localStorage.getItem('gameProgress') || '{}');
      const completedStories: string[] = savedProgress.completedStories || [];
      const unlockedStories: string[] = savedProgress.unlockedStories || [];

      const casesData = await realCasesService.loadRealCases();
      const baseStories: any[] = [];
      Object.values(casesData.turtleSoup).forEach((arr: any) => {
        if (Array.isArray(arr)) baseStories.push(...arr);
      });
      const baseStoryIds = new Set(
        baseStories.map(story => story.id).filter(Boolean) as string[]
      );

      const baseTotal = baseStoryIds.size || baseStories.length;
      const baseCompletedCount = completedStories.filter(id => baseStoryIds.has(id)).length;
      const baseUnlockedCount = unlockedStories.filter(id => baseStoryIds.has(id)).length;

      setTotalCount(baseTotal);
      setCompletedCount(baseCompletedCount);
      setUnlockedCount(baseUnlockedCount);
      setProgressPercentage(baseTotal > 0 ? Math.round((baseCompletedCount / baseTotal) * 100) : 0);
    };

    loadProgress();
  }, []);

  const handleLoginSuccess = () => {
    const user = authService.getCurrentUser();
    if (user) setCurrentUser(user);
  };

  const handleLogout = () => {
    authService.logout();
    setCurrentUser(null);
  };

  const features = [
    {
      icon: <Turtle className="w-8 h-8" />,
      title: '海龟汤游戏',
      description: `经典推理游戏，通过提问找出故事真相${unlockedCount > 0 ? ` (已解锁${unlockedCount}个故事)` : ''}`,
      color: 'from-ocean-400 to-ocean-600'
    },
    {
      icon: <Book className="w-8 h-8" />,
      title: '主线剧情',
      description: '一名新警的成长故事，揭开扑朔迷底',
      color: 'from-purple-400 to-purple-600'
    },
    {
      icon: <Zap className="w-8 h-8" />,
      title: 'AI主持人',
      description: '智能AI主持，提供公平的游戏体验',
      color: 'from-warm-400 to-warm-600'
    },
    {
      icon: <Users className="w-8 h-8" />,
      title: '多人互动',
      description: '实时聊天，与其他玩家一起推理',
      color: 'from-gray-400 to-gray-600'
    }
  ];

  return (
    <div className={`min-h-screen transition-colors duration-300 ${isDark ? 'bg-gray-900' : 'bg-gradient-to-br from-turtle-50 to-white'}`}>
      {/* 导航栏 */}
      <nav className={`sticky top-0 z-50 border-b backdrop-blur-md transition-colors duration-300 ${isDark ? 'bg-gray-800/80 border-gray-700' : 'bg-white/70 border-gray-200'}`}>
        <div className="container mx-auto px-4 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-2">
              <Turtle className={`w-8 h-8 ${isDark ? 'text-ocean-400' : 'text-ocean-600'}`} />
              <h1 className={`text-2xl font-bold ${isDark ? 'text-ocean-400' : 'text-ocean-600'}`}>法底捞针</h1>
            </div>
            <div className="flex items-center space-x-2 md:space-x-3 flex-wrap justify-end">
              {/* 主题切换 */}
              <button
                onClick={toggleTheme}
                className={`p-2 rounded-lg transition-colors ${isDark ? 'bg-gray-700 text-yellow-400 hover:bg-gray-600' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                title={isDark ? '切换到亮色模式' : '切换到暗色模式'}
              >
                {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
              </button>

              {/* 进度 */}
              <span className={`text-sm hidden lg:inline ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>已完成 {completedCount} / {totalCount} 个故事</span>
              <div className={`w-24 rounded-full h-2 hidden lg:block ${isDark ? 'bg-gray-700' : 'bg-gray-200'}`}>
                <div
                  className="h-full bg-gradient-to-r from-ocean-500 to-green-500 rounded-full transition-all duration-500"
                  style={{ width: `${progressPercentage}%` }}
                />
              </div>
              <span className={`text-sm font-medium hidden lg:inline ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{progressPercentage}%</span>

              {/* 功能按钮 - 始终显示 */}
              <button
                onClick={() => navigate('/dashboard')}
                className={`flex items-center space-x-1 px-3 py-1.5 rounded-lg transition-colors text-sm ${isDark ? 'bg-ocean-900/50 text-ocean-300 hover:bg-ocean-800/50' : 'bg-ocean-50 text-ocean-700 hover:bg-ocean-100'}`}
                title="我的仪表盘"
              >
                <LayoutDashboard className="w-4 h-4" />
                <span className="hidden md:inline">仪表盘</span>
              </button>

              <button
                onClick={() => navigate('/history')}
                className={`flex items-center space-x-1 px-3 py-1.5 rounded-lg transition-colors text-sm ${isDark ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                title="历史记录"
              >
                <History className="w-4 h-4" />
                <span className="hidden md:inline">历史</span>
              </button>

              <button
                onClick={() => navigate('/multiplayer')}
                className={`flex items-center space-x-1 px-3 py-1.5 rounded-lg transition-colors text-sm ${isDark ? 'bg-purple-900/50 text-purple-300 hover:bg-purple-800/50' : 'bg-purple-50 text-purple-700 hover:bg-purple-100'}`}
                title="多人联机"
              >
                <Users className="w-4 h-4" />
                <span className="hidden md:inline">联机</span>
              </button>

              <button
                onClick={() => navigate('/leaderboard')}
                className={`flex items-center space-x-1 px-3 py-1.5 rounded-lg transition-colors text-sm ${isDark ? 'bg-amber-900/50 text-amber-300 hover:bg-amber-800/50' : 'bg-amber-50 text-amber-700 hover:bg-amber-100'}`}
                title="排行榜"
              >
                <Trophy className="w-4 h-4" />
                <span className="hidden md:inline">排行</span>
              </button>

              <button
                onClick={() => navigate('/cards')}
                className={`flex items-center space-x-1 px-3 py-1.5 rounded-lg transition-colors text-sm ${isDark ? 'bg-emerald-900/50 text-emerald-300 hover:bg-emerald-800/50' : 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100'}`}
                title="我的知识卡片"
              >
                <Bookmark className="w-4 h-4" />
                <span className="hidden md:inline">知识卡片</span>
              </button>

              <button
                onClick={() => navigate('/manual')}
                className={`flex items-center space-x-1 px-3 py-1.5 rounded-lg transition-colors text-sm ${isDark ? 'bg-red-900/50 text-red-300 hover:bg-red-800/50' : 'bg-red-50 text-red-700 hover:bg-red-100'}`}
                title="防骗速查手册"
              >
                <ShieldCheck className="w-4 h-4" />
                <span className="hidden md:inline">防骗手册</span>
              </button>

              {/* 用户区域 */}
              {currentUser ? (
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => navigate('/profile')}
                    className={`flex items-center space-x-1 px-3 py-1.5 rounded-lg transition-colors text-sm ${isDark ? 'bg-green-900/50 text-green-300 hover:bg-green-800/50' : 'bg-green-50 text-green-700 hover:bg-green-100'}`}
                    title="个人资料"
                  >
                    <UserCircle className="w-4 h-4" />
                    <span className="hidden md:inline">{currentUser.username}</span>
                  </button>
                  <button
                    onClick={handleLogout}
                    className={`p-2 rounded-lg transition-colors ${isDark ? 'text-gray-400 hover:text-red-400 hover:bg-gray-700' : 'text-gray-600 hover:text-red-600 hover:bg-gray-100'}`}
                    title="退出登录"
                  >
                    <LogOut className="w-5 h-5" />
                  </button>
                </div>
              ) : (
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => setShowLoginModal(true)}
                    className={`flex items-center space-x-1 px-3 py-1.5 rounded-lg transition-colors text-sm ${isDark ? 'bg-ocean-900/50 text-ocean-300 hover:bg-ocean-800/50' : 'bg-ocean-50 text-ocean-700 hover:bg-ocean-100'}`}
                  >
                    <LogIn className="w-4 h-4" />
                    <span className="hidden md:inline">登录</span>
                  </button>
                  <button
                    onClick={() => setShowLoginModal(true)}
                    className={`flex items-center space-x-1 px-3 py-1.5 rounded-lg transition-colors text-sm ${isDark ? 'bg-warm-900/50 text-warm-300 hover:bg-warm-800/50' : 'bg-warm-50 text-warm-700 hover:bg-warm-100'}`}
                  >
                    <span>注册</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* 主要内容 */}
      <main>
        {/* Hero区域 */}
        <section className="container mx-auto px-4 py-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-4xl mx-auto"
          >
            <h2 className={`text-4xl md:text-5xl font-bold mb-6 ${isDark ? 'text-white' : 'text-gray-700'}`}>
              基于AI智能主持人的互动反诈网站
            </h2>
            <p className={`text-xl mb-8 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
              通过精彩的推理故事，学习法律知识，体验智慧碰撞的乐趣
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="btn-primary text-lg px-8 py-4"
                onClick={() => navigate('/game')}
              >
                <Play className="inline-block w-5 h-5 mr-2" />
                立即开始
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-gradient-to-r from-purple-500 to-purple-700 text-white text-lg px-8 py-4 rounded-lg hover:from-purple-600 hover:to-purple-800 transition"
                onClick={() => navigate('/dual-story')}
              >
                <Book className="inline-block w-5 h-5 mr-2" />
                主线剧情
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="btn-secondary text-lg px-8 py-4"
                onClick={() => navigate('/stories')}
              >
                <BookOpen className="inline-block w-5 h-5 mr-2" />
                题库总览
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-gradient-to-r from-warm-500 to-warm-700 text-white text-lg px-8 py-4 rounded-lg hover:from-warm-600 hover:to-warm-800 transition"
                onClick={() => navigate('/scenario/flightDelay')}
              >
                <ShieldCheck className="inline-block w-5 h-5 mr-2" />
                情景模拟
              </motion.button>
            </div>
          </motion.div>
        </section>

        {/* 今日挑战 */}
        {dailyChallenge && (
          <section className="container mx-auto px-4 pb-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className={`max-w-3xl mx-auto rounded-2xl p-6 shadow-lg border-2 ${
                isDark ? 'bg-gray-800 border-amber-700/50' : 'bg-white border-amber-200'
              }`}
            >
              <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <CalendarDays className="w-5 h-5 text-amber-500" />
                    <span className={`text-sm font-medium ${isDark ? 'text-amber-300' : 'text-amber-600'}`}>
                      今日挑战 · {dailyChallenge.date}
                    </span>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>
                      {dailyChallenge.category}
                    </span>
                    {dailyDone && (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">
                        🏅 今日已完成
                      </span>
                    )}
                  </div>
                  <h3 className={`text-xl font-bold mb-1 ${isDark ? 'text-white' : 'text-gray-800'}`}>
                    {dailyChallenge.title}
                  </h3>
                  <p className={`text-sm line-clamp-2 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                    {dailyChallenge.surface}
                  </p>
                  {badgeCount > 0 && (
                    <p className={`mt-2 text-xs flex items-center gap-1 ${isDark ? 'text-amber-300/80' : 'text-amber-600/80'}`}>
                      <Award className="w-3.5 h-3.5" />
                      已收集 {badgeCount} 枚反诈徽章，继续加油！
                    </p>
                  )}
                </div>
                <button
                  onClick={() => navigate(`/game?story=${dailyChallenge.storyId}`)}
                  className={`shrink-0 px-6 py-3 rounded-lg text-white font-medium transition shadow ${
                    dailyDone
                      ? 'bg-amber-500 hover:bg-amber-600'
                      : 'bg-gradient-to-r from-amber-500 to-orange-500 hover:opacity-90'
                  }`}
                >
                  {dailyDone ? '再玩一次' : '立即挑战'}
                </button>
              </div>
            </motion.div>
          </section>
        )}

        {/* 特性展示 */}
        <section className={`py-20 ${isDark ? 'bg-gray-800/50' : 'wave-decoration'}`}>
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h3 className={`text-4xl font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-700'}`}>
                游戏特色
              </h3>
              <p className={`text-lg ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                体验全新的海龟汤游戏方式
              </p>
            </motion.div>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {features.map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className={`text-center group hover:scale-105 transition-transform rounded-2xl p-6 ${isDark ? 'bg-gray-800 border border-gray-700' : 'card'}`}
                >
                  <div className={`inline-flex p-4 rounded-full bg-gradient-to-r ${feature.color} text-white mb-4 group-hover:animate-pulse-slow`}>
                    {feature.icon}
                  </div>
                  <h4 className={`text-xl font-semibold mb-2 ${isDark ? 'text-white' : ''}`}>{feature.title}</h4>
                  <p className={isDark ? 'text-gray-400' : 'text-gray-600'}>{feature.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* 游戏流程 */}
        <section className={`py-20 ${isDark ? 'bg-gray-900' : 'bg-gray-50'}`}>
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h3 className={`text-4xl font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-700'}`}>
                游戏流程
              </h3>
              <p className={`text-lg ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                简单四步，开始你的推理之旅
              </p>
            </motion.div>
            <div className="grid md:grid-cols-4 gap-8">
              {[
                { step: 1, title: '选择模式', desc: '单人推理或多人联机协作' },
                { step: 2, title: '选择题目', desc: '选择一个有趣的海龟汤题目' },
                { step: 3, title: '开始推理', desc: '通过提问逐步揭开真相' },
                { step: 4, title: '获得胜利', desc: '一直推测直到抵达真相' }
              ].map((item) => (
                <motion.div
                  key={item.step}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  className="text-center"
                >
                  <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-r from-ocean-500 to-ocean-600 text-white rounded-full flex items-center justify-center text-2xl font-bold">
                    {item.step}
                  </div>
                  <h4 className={`text-lg font-semibold mb-2 ${isDark ? 'text-white' : ''}`}>{item.title}</h4>
                  <p className={isDark ? 'text-gray-400' : 'text-gray-600'}>{item.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      </main>

      {/* 页脚 */}
      <footer className={`py-12 ${isDark ? 'bg-gray-800 text-gray-300' : 'bg-gray-900 text-white'}`}>
        <div className="container mx-auto px-4 text-center">
          <div className="flex items-center justify-center space-x-2 mb-4">
            <Turtle className="w-6 h-6" />
            <span className="text-xl font-semibold">法底捞针</span>
          </div>
          <p className="text-gray-400 mb-4">
            让推理更有趣，让法律更生动
          </p>
          <div className="text-sm text-gray-500">
            © 2026 法底捞针2.0
          </div>
        </div>
      </footer>

      {/* 登录弹窗 */}
      <LoginModal
        isOpen={showLoginModal}
        onClose={() => setShowLoginModal(false)}
        onLoginSuccess={handleLoginSuccess}
      />
    </div>
  );
};

export default HomePage;
